export type DayOfWeek = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export const DAYS_OF_WEEK: { id: DayOfWeek; label: string; short: string }[] = [
  { id: "mon", label: "Monday", short: "Mon" },
  { id: "tue", label: "Tuesday", short: "Tue" },
  { id: "wed", label: "Wednesday", short: "Wed" },
  { id: "thu", label: "Thursday", short: "Thu" },
  { id: "fri", label: "Friday", short: "Fri" },
  { id: "sat", label: "Saturday", short: "Sat" },
  { id: "sun", label: "Sunday", short: "Sun" },
];

export type NotificationCategory =
  | "mentions"
  | "task_assignments"
  | "task_status"
  | "due_dates"
  | "portal_comments"
  | "commercial";

export const NOTIFICATION_CATEGORIES: { id: NotificationCategory; label: string; description: string }[] = [
  { id: "mentions", label: "Mentions", description: "@mentions of you in task descriptions and comments." },
  { id: "task_assignments", label: "Task Assignments", description: "Tasks assigned or reassigned to you." },
  { id: "task_status", label: "Task Status Changes", description: "Tasks marked blocked, waiting on client, or completed." },
  { id: "due_dates", label: "Deadlines & Due Dates", description: "Tasks due today or approaching deadline." },
  { id: "portal_comments", label: "Client Portal Activity", description: "New comments posted by clients in the client portal." },
  { id: "commercial", label: "Commercial & Budget Alerts", description: "Funding bucket thresholds and budget drawdown warnings." },
];

export type NotificationChannel =
  | "web"
  | "web_push"
  | "slack_dm"
  | "slack_channel"
  | "email_instant"
  | "email_digest";

export const NOTIFICATION_CHANNELS: { id: NotificationChannel; label: string; short: string }[] = [
  { id: "web", label: "Web Inbox", short: "In-App" },
  { id: "web_push", label: "Browser Push", short: "Push" },
  { id: "slack_dm", label: "Slack DM", short: "Slack" },
  { id: "email_instant", label: "Email (Instant)", short: "Email" },
  { id: "email_digest", label: "Daily Digest", short: "Digest" },
];

export interface QuietHoursSchedule {
  id: string;
  name: string;
  enabled: boolean;
  days: DayOfWeek[];
  start_time: string; // "18:30" (24h format HH:mm)
  end_time: string;   // "08:30" (24h format HH:mm)
  timezone?: string;
  allow_urgent_override: boolean;
  action: "queue_for_digest" | "mute" | "drop";
}

export interface UserNotificationSettings {
  timezone: string;
  quiet_hours: QuietHoursSchedule[];
  channels: Record<NotificationCategory, Record<NotificationChannel, boolean>>;
  digest: {
    enabled: boolean;
    delivery_time: string; // "08:30"
    days: DayOfWeek[];
    channel: "email" | "slack_dm";
  };
  web_push_enabled: boolean;
}

export function defaultNotificationSettings(userTimezone?: string): UserNotificationSettings {
  const tz = userTimezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "America/New_York");

  return {
    timezone: tz,
    quiet_hours: [
      {
        id: "weeknights",
        name: "Weeknights",
        enabled: true,
        days: ["mon", "tue", "wed", "thu", "fri"],
        start_time: "18:30",
        end_time: "08:30",
        timezone: tz,
        allow_urgent_override: true,
        action: "queue_for_digest",
      },
      {
        id: "weekends",
        name: "Weekends",
        enabled: true,
        days: ["sat", "sun"],
        start_time: "00:00",
        end_time: "23:59",
        timezone: tz,
        allow_urgent_override: true,
        action: "queue_for_digest",
      },
    ],
    channels: {
      mentions: { web: true, web_push: true, slack_dm: true, slack_channel: false, email_instant: true, email_digest: false },
      task_assignments: { web: true, web_push: true, slack_dm: true, slack_channel: false, email_instant: false, email_digest: true },
      task_status: { web: true, web_push: false, slack_dm: false, slack_channel: false, email_instant: false, email_digest: true },
      due_dates: { web: true, web_push: true, slack_dm: false, slack_channel: false, email_instant: false, email_digest: true },
      portal_comments: { web: true, web_push: true, slack_dm: true, slack_channel: true, email_instant: false, email_digest: false },
      commercial: { web: true, web_push: false, slack_dm: true, slack_channel: false, email_instant: true, email_digest: false },
    },
    digest: {
      enabled: true,
      delivery_time: "08:30",
      days: ["mon", "tue", "wed", "thu", "fri"],
      channel: "email",
    },
    web_push_enabled: false,
  };
}

/**
 * Checks if a time "HH:mm" falls within [start, end].
 * Handles cross-midnight spans (e.g. 18:30 to 08:30).
 */
export function isTimeInInterval(timeStr: string, startStr: string, endStr: string): boolean {
  const [h, m] = timeStr.split(":").map(Number);
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = endStr.split(":").map(Number);

  const t = h * 60 + m;
  const start = sh * 60 + sm;
  const end = eh * 60 + em;

  if (start <= end) {
    return t >= start && t <= end;
  }
  // Crosses midnight (e.g. 18:30 -> 08:30)
  return t >= start || t <= end;
}

const JS_DAY_TO_DAY_OF_WEEK: DayOfWeek[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function getDayOfWeekFromDate(date: Date, timezone?: string): { day: DayOfWeek; timeStr: string } {
  try {
    const tz = timezone || "UTC";
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = formatter.formatToParts(date);
    const weekdayPart = parts.find((p) => p.type === "weekday")?.value?.toLowerCase();
    const hourPart = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minutePart = parts.find((p) => p.type === "minute")?.value ?? "00";

    const dayMap: Record<string, DayOfWeek> = {
      mon: "mon", tue: "tue", wed: "wed", thu: "thu", fri: "fri", sat: "sat", sun: "sun",
    };
    const day = (weekdayPart ? dayMap[weekdayPart.slice(0, 3)] : undefined) ?? JS_DAY_TO_DAY_OF_WEEK[date.getUTCDay()];
    return { day, timeStr: `${hourPart.padStart(2, "0")}:${minutePart.padStart(2, "0")}` };
  } catch {
    const day = JS_DAY_TO_DAY_OF_WEEK[date.getDay()];
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return { day, timeStr: `${h}:${m}` };
  }
}

/**
 * Checks if the given date/time is currently in quiet hours according to any active schedule.
 */
export function isInQuietHours(
  schedules: QuietHoursSchedule[],
  date: Date = new Date(),
  defaultTimezone?: string,
): { inQuiet: boolean; matchedSchedule?: QuietHoursSchedule } {
  for (const s of schedules) {
    if (!s.enabled) continue;
    const tz = s.timezone || defaultTimezone;
    const { day, timeStr } = getDayOfWeekFromDate(date, tz);

    if (s.days.includes(day)) {
      if (isTimeInInterval(timeStr, s.start_time, s.end_time)) {
        return { inQuiet: true, matchedSchedule: s };
      }
    }
  }
  return { inQuiet: false };
}

export interface DeliveryDecision {
  deliverImmediately: NotificationChannel[];
  queueForDigest: NotificationChannel[];
  muted: NotificationChannel[];
  inQuietHours: boolean;
  urgentOverrideApplied: boolean;
}

/**
 * Calculates channel routing for an incoming notification given user settings and quiet hours.
 */
export function evaluateNotificationDelivery(params: {
  category: NotificationCategory;
  priority?: "normal" | "high" | "urgent";
  userSettings: UserNotificationSettings;
  date?: Date;
}): DeliveryDecision {
  const { category, priority = "normal", userSettings, date = new Date() } = params;
  const configured = userSettings.channels[category] || defaultNotificationSettings().channels[category];
  const enabledChannels = (Object.keys(configured) as NotificationChannel[]).filter((ch) => configured[ch]);

  const { inQuiet, matchedSchedule } = isInQuietHours(userSettings.quiet_hours, date, userSettings.timezone);

  if (!inQuiet) {
    return {
      deliverImmediately: enabledChannels,
      queueForDigest: [],
      muted: [],
      inQuietHours: false,
      urgentOverrideApplied: false,
    };
  }

  // Handle Quiet Hours
  const isUrgent = priority === "urgent";
  if (isUrgent && matchedSchedule?.allow_urgent_override) {
    return {
      deliverImmediately: enabledChannels,
      queueForDigest: [],
      muted: [],
      inQuietHours: true,
      urgentOverrideApplied: true,
    };
  }

  const action = matchedSchedule?.action ?? "queue_for_digest";
  const deliverImmediately: NotificationChannel[] = [];
  const queueForDigest: NotificationChannel[] = [];
  const muted: NotificationChannel[] = [];

  for (const ch of enabledChannels) {
    if (ch === "web") {
      // In-app notifications are always logged into the inbox silently
      deliverImmediately.push(ch);
    } else if (action === "queue_for_digest") {
      queueForDigest.push(ch);
    } else {
      muted.push(ch);
    }
  }

  return {
    deliverImmediately,
    queueForDigest,
    muted,
    inQuietHours: true,
    urgentOverrideApplied: false,
  };
}

export function validateQuietSchedule(schedule: Partial<QuietHoursSchedule>): { valid: boolean; error?: string } {
  if (!schedule.name || schedule.name.trim().length === 0) {
    return { valid: false, error: "Schedule name is required." };
  }
  if (!schedule.days || schedule.days.length === 0) {
    return { valid: false, error: "At least one day must be selected." };
  }
  const timeRegex = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!schedule.start_time || !timeRegex.test(schedule.start_time)) {
    return { valid: false, error: "Start time must be formatted as HH:mm (e.g. 18:30)." };
  }
  if (!schedule.end_time || !timeRegex.test(schedule.end_time)) {
    return { valid: false, error: "End time must be formatted as HH:mm (e.g. 08:30)." };
  }
  return { valid: true };
}
